import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, MapPin, Users, ShoppingCart, Loader2, Sparkles, ChevronDown, ChevronUp, Info, Sun, Fish, XCircle, ListChecks } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ContactBanner from '../components/ContactBanner';
import axios from 'axios';
import { usePageHeader } from '../hooks/usePageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EventsPage = () => {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const header = usePageHeader('events', { heading: 'Special Events', subheading: 'Join us for unique diving experiences, social gatherings, and special occasions throughout the year.' });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate discount based on membership
  const getDiscountedPrice = (price) => {
    if (user?.membership_tier === 'pro') {
      return price * 0.75; // 25% off
    } else if (user?.membership_tier === 'club') {
      return price * 0.80; // 20% off
    }
    return price;
  };

  const discountPercent = user?.membership_tier === 'pro' ? 25 : user?.membership_tier === 'club' ? 20 : 0;

  const handleAddToCart = (event) => {
    const cartItem = {
      id: event.id,
      product_id: event.id,
      name: event.name,
      price: event.price,
      description: event.description,
      image_url: event.image_url || "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=300&fit=crop",
      category: "events"
    };
    addToCart(cartItem);
    toast.success(`${event.name} added to cart`);
  };

  const handleAddSessionToCart = (event, session, idx) => {
    const sessionPrice = session.price !== null && session.price !== undefined && session.price !== ''
      ? Number(session.price)
      : event.price;
    const cartItem = {
      id: `${event.id}__s${idx}`,
      product_id: event.id,
      session_index: idx,
      name: `${event.name} — ${session.title}`,
      price: sessionPrice,
      description: `${session.date || ''}${session.time ? ' at ' + session.time : ''}`.trim(),
      image_url: event.image_url || "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=300&fit=crop",
      category: "events"
    };
    addToCart(cartItem);
    toast.success(`${session.title} added to cart`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 text-[#FF6B35]" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342]">
              {header.heading}
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl">
            {header.subheading}
          </p>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border">
            <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Upcoming Events</h3>
            <p className="text-gray-500">Check back soon for special events and gatherings!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const discountedPrice = getDiscountedPrice(event.price);
              const hasDiscount = discountPercent > 0;
              const isExpanded = expandedEvent === event.id;

              const toggleExpand = () => {
                setExpandedEvent(isExpanded ? null : event.id);
              };

              return (
                <div 
                  key={event.id} 
                  className="bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-48 overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center" onClick={toggleExpand}>
                    <img 
                      src={event.image_url || "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop"} 
                      alt={event.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-[#FF6B35] text-white">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Special Event
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <Badge className="bg-white/90 text-[#0A2342] backdrop-blur-sm">
                        Click for details
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-semibold text-[#0A2342] mb-2">
                      {event.name}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4">
                      {event.description}
                    </p>
                    
                    {/* Expand/Collapse Button */}
                    <button 
                      onClick={toggleExpand}
                      className="flex items-center gap-2 text-[#FF6B35] font-medium text-sm mb-4 hover:underline"
                      data-testid={`expand-event-${event.id}`}
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View More Details
                        </>
                      )}
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="bg-[#F0EFEA] rounded-lg p-4 mb-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <h4 className="font-semibold text-[#0A2342] border-b border-[#0A2342]/10 pb-2">Event Details</h4>
                        
                        {event.what_to_expect && (
                          <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-[#0A2342] text-sm">What to Expect</p>
                              <p className="text-gray-600 text-sm whitespace-pre-line">{event.what_to_expect}</p>
                            </div>
                          </div>
                        )}
                        
                        {event.typical_weather && (
                          <div className="flex items-start gap-3">
                            <Sun className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-[#0A2342] text-sm">Typical Weather</p>
                              <p className="text-gray-600 text-sm">{event.typical_weather}</p>
                            </div>
                          </div>
                        )}
                        
                        {event.highlights && (
                          <div className="flex items-start gap-3">
                            <Fish className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-[#0A2342] text-sm">Highlights</p>
                              <p className="text-gray-600 text-sm whitespace-pre-line">{event.highlights}</p>
                            </div>
                          </div>
                        )}

                        {event.sessions && event.sessions.length > 0 && (
                          <div className="flex items-start gap-3">
                            <ListChecks className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-medium text-[#0A2342] text-sm mb-2">Book Individual Sessions</p>
                              <ul className="space-y-2">
                                {event.sessions.map((s, i) => (
                                  <li key={i} className="text-sm text-gray-700 border-l-2 border-[#FF6B35]/40 pl-3 flex items-start justify-between gap-2" data-testid={`event-session-${event.id}-${i}`}>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-[#0A2342]">{s.title}</div>
                                      <div className="text-xs text-gray-600 flex items-center gap-2 flex-wrap">
                                        {s.date && <span><Calendar className="w-3 h-3 inline mr-1" />{s.date}{s.end_date && ` - ${s.end_date}`}</span>}
                                        {s.time && <span><Clock className="w-3 h-3 inline mr-1" />{s.time}</span>}
                                        {s.price !== null && s.price !== undefined && s.price !== '' && (
                                          <span className="font-semibold text-[#0A2342]">R{Number(s.price).toLocaleString()}</span>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white h-7 px-2 text-xs whitespace-nowrap"
                                      onClick={() => handleAddSessionToCart(event, s, i)}
                                      data-testid={`book-session-${event.id}-${i}`}
                                    >
                                      <ShoppingCart className="w-3 h-3 mr-1" />
                                      Book
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                              <p className="text-xs text-gray-500 mt-2 italic">Or book the full combo below.</p>
                            </div>
                          </div>
                        )}

                        {event.does_not_include && (
                          <div className="flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-[#0A2342] text-sm">Does Not Include</p>
                              <p className="text-gray-600 text-sm whitespace-pre-line">{event.does_not_include}</p>
                            </div>
                          </div>
                        )}

                        {!event.what_to_expect && !event.typical_weather && !event.highlights && (!event.sessions || event.sessions.length === 0) && !event.does_not_include && (
                          <p className="text-gray-500 text-sm italic">More details coming soon. Contact us for more information about this event.</p>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-[#FF6B35]" />
                        <span>{event.event_date}{event.end_date && ` - ${event.end_date}`}</span>
                      </div>
                      {event.event_time && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-[#FF6B35]" />
                          <span>{event.event_time}</span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-[#FF6B35]" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.max_participants && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users className="w-4 h-4 text-[#FF6B35]" />
                          <span>Max {event.max_participants} participants</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        {event.price_starting_from && (
                          <div className="text-xs text-gray-500 leading-none mb-1">Starting from</div>
                        )}
                        {hasDiscount ? (
                          <div>
                            <span className="text-sm text-gray-400 line-through">
                              R{event.price.toLocaleString()}
                            </span>
                            <span className="text-xl font-mono-accent font-bold text-[#FF6B35] ml-2">
                              R{discountedPrice.toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xl font-mono-accent font-bold text-[#0A2342]">
                            R{event.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={() => handleAddToCart(event)}
                        className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                        data-testid={`book-event-${event.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {event.sessions && event.sessions.length > 0 ? 'Book Combo' : 'Book Now'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Banner */}
        <div className="mt-12">
          <ContactBanner whatsappNumber="+27 67 535 5405" />
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
